import {
  useActiveAuthProvider,
  useLogout,
  useRefineOptions,
} from '@refinedev/core'
import { LogOutIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { MyLanguageMenu } from '~/components/refineUi/layout/myLanguageMenu'
import { UserAvatar } from '~/components/refineUi/layout/userAvatar'
import { ThemeToggle } from '~/components/refineUi/theme/themeToggle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdownMenu'
import { SidebarTrigger, useSidebar } from '~/components/ui/sidebar'
import { cn } from '~/lib/utils'

type HeaderProps = {
  /** e.g. the tenant switcher, before the theme toggle and user menu */
  start?: ReactNode
}

export const Header = ({ start }: HeaderProps) => {
  const { isMobile } = useSidebar()

  return (
    <>
      {isMobile ? (
        <MobileHeader start={start} />
      ) : (
        <DesktopHeader start={start} />
      )}
    </>
  )
}

function DesktopHeader({ start }: HeaderProps) {
  return (
    <header
      className={cn(
        'sticky',
        'top-0',
        'flex',
        'h-16',
        'shrink-0',
        'items-center',
        'gap-4',
        'border-b',
        'border-border',
        'bg-sidebar',
        'pr-3',
        'justify-end',
        'z-40',
      )}
    >
      {start}
      <ThemeToggle />
      <UserDropdown />
    </header>
  )
}

function MobileHeader({ start }: HeaderProps) {
  const { open, isMobile } = useSidebar()

  const { title } = useRefineOptions()

  return (
    <header
      className={cn(
        'sticky',
        'top-0',
        'flex',
        'h-12',
        'shrink-0',
        'items-center',
        'gap-2',
        'border-b',
        'border-border',
        'bg-sidebar',
        'pr-3',
        'justify-between',
        'z-40',
      )}
    >
      <SidebarTrigger
        className={cn('text-muted-foreground', 'rotate-180', 'ml-1', {
          'opacity-0': open,
          'opacity-100': !open || isMobile,
          'pointer-events-auto': !open || isMobile,
          'pointer-events-none': open && !isMobile,
        })}
      />

      <div
        className={cn(
          'whitespace-nowrap',
          'flex',
          'flex-row',
          'h-full',
          'items-center',
          'justify-start',
          'gap-2',
          'transition-discrete',
          'duration-200',
          {
            'pl-3': !open,
            'pl-5': open,
          },
        )}
      >
        <div>{title.icon}</div>
        <h2
          className={cn(
            'text-sm',
            'font-bold',
            'transition-opacity',
            'duration-200',
            {
              'opacity-0': !open,
              'opacity-100': open,
            },
          )}
        >
          {title.text}
        </h2>
      </div>

      <div className={cn('flex', 'items-center', 'gap-2')}>
        {start}
        <ThemeToggle className={cn('h-8', 'w-8')} />
      </div>
    </header>
  )
}

const UserDropdown = () => {
  const { mutate: logout, isPending: isLoggingOut } = useLogout()
  const { t } = useTranslation()

  const authProvider = useActiveAuthProvider()

  if (!authProvider?.getIdentity) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label={t('shell.userMenu.label')}>
        <UserAvatar />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <MyLanguageMenu />
        <DropdownMenuItem
          onClick={() => {
            logout()
          }}
        >
          <LogOutIcon
            className={cn('text-destructive', 'hover:text-destructive')}
          />
          <span className={cn('text-destructive', 'hover:text-destructive')}>
            {isLoggingOut
              ? t('shell.userMenu.loggingOut')
              : t('shell.userMenu.logout')}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

Header.displayName = 'Header'
MobileHeader.displayName = 'MobileHeader'
DesktopHeader.displayName = 'DesktopHeader'
